import { FieldPath } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { editEngageDomain, getEngageDomains } from 'api/engage';
import * as Drawer from 'components/drawer';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { GivebutterAxiosError } from 'types/error';

interface EditDomainFormValues {
    reply_to: string;
}

const EditDomainDrawer = () => {
    const toast = useToast();
    const { accountId } = useCurrentAccount();
    const history = useHistory();
    const { domain_id } = useParams<{ domain_id: string }>();

    const { data: domain } = useSuspenseQuery({
        queryKey: accountQueryKeys.engageDomains(accountId),
        queryFn: () => {
            return getEngageDomains(accountId);
        },
        select: response => {
            return (
                response.data.data.filter(domain => {
                    return domain_id === domain.id.toString();
                })[0] ?? undefined
            );
        },
    });

    const methods = Form.useForm<EditDomainFormValues>({
        defaultValues: {
            reply_to: domain?.reply_to ?? '',
        },
    });
    const { handleSubmit, control, resetFormButton, path, setError } = methods;
    const { mutate: submit } = useMutation({
        mutationFn: ({ reply_to }: EditDomainFormValues) =>
            editEngageDomain(accountId, domain?.id ?? 0, { reply_to }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.engageDomains(accountId),
            });
            toast.success(<>Successfully updated domain</>);
            history.push(`/accounts/${accountId}/engage/domains`);
        },
        onError: (error: GivebutterAxiosError) => {
            const errors = error.response?.data.errors;
            if (errors) {
                Object.keys(errors).forEach(key => {
                    setError(key as FieldPath<EditDomainFormValues>, {
                        message: errors[key][0],
                    });
                });
            } else {
                toast.error('Unknown error editing domain');
            }
            resetFormButton();
        },
    });

    return (
        <Form.Provider {...methods}>
            <form className={'contents'} onSubmit={handleSubmit(values => submit(values))}>
                <Drawer.Header>
                    <Drawer.Title>Edit domain</Drawer.Title>
                </Drawer.Header>
                <Drawer.Body>
                    <div>
                        <div className="font-medium">
                            <Form.Container>
                                <Form.Label name={path('reply_to')}>
                                    Reply-to email address (optional)
                                </Form.Label>
                                <Form.TextInputRoot name="reply_to" control={control}>
                                    <Form.TextInputForm placeholder="reply@example.org" />
                                </Form.TextInputRoot>
                                <Form.Error name={path('reply_to')} />
                            </Form.Container>
                        </div>
                    </div>
                </Drawer.Body>
                <Drawer.Footer>
                    <div
                        className={
                            'flex w-full flex-wrap-reverse items-center justify-between gap-2'
                        }
                    >
                        <Drawer.Cancel asChild={true}>
                            <Button variant="transparent">Cancel</Button>
                        </Drawer.Cancel>
                        <Form.SubmitButton>Save</Form.SubmitButton>
                    </div>
                </Drawer.Footer>
            </form>
        </Form.Provider>
    );
};

export { EditDomainDrawer };
